import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { gql, GraphQLAPI, GraphQLResult } from "@examspark/api";
import { exists } from "@examspark/common-util";
import { CreateAppUserConfigInput } from "@examspark/types";

export const useQueryGetApp = (appId?: string) => {
  return useQuery({
    enabled: !!appId,
    queryKey: ["app", appId],
    queryFn: async () => gql.operations.app.getApp(appId || ""),
  });
};

export const useQueryGetAppLicense = (appId?: string, workspaceId?: string) => {
  return useQuery({
    enabled: !!appId && !!workspaceId,
    queryKey: ["app-license", appId, workspaceId],
    queryFn: async () =>
      gql.operations.app.getAppLicense(appId || "", workspaceId || ""),
  });
};

export const useQueryGetAppKey = (appKeyId?: string) => {
  return useQuery({
    enabled: !!appKeyId,
    queryKey: ["app-key", appKeyId],
    queryFn: async () => gql.operations.app.getAppKey(appKeyId || ""),
  });
};

export const useQueryGetAppDomain = (domain: string | undefined) => {
  return useQuery({
    enabled: !!domain,
    queryKey: ["app-domain", domain],
    queryFn: async () => gql.operations.app.getAppDomain(domain || ""),
  });
};

export const useQueryGetAppKeyCredentials = (appKeyId?: string) => {
  return useQuery({
    enabled: !!appKeyId,
    queryKey: ["app-key-credentials", appKeyId],
    queryFn: async () =>
      gql.operations.app.getAppKeyCredentials(appKeyId || ""),
  });
};

export const useQueryListAppUserConfigs = (appKeyId?: string) => {
  return useQuery({
    queryKey: ["app-user-config", appKeyId],
    queryFn: () => gql.operations.app.listAppUserConfigs(appKeyId),
    retry: (failureCount, error: GraphQLResult) => {
      if (
        error.errors?.some((e) =>
          e.message.includes("does not have permission to manage"),
        )
      ) {
        return false;
      }
      return failureCount < 5;
    },
  });
};

export const useQueryListApps = (workspaceId: string | undefined) => {
  return useQuery({
    queryKey: ["apps", workspaceId],
    enabled: workspaceId != null,
    queryFn: async () => {
      if (workspaceId == null) return;

      const result =
        await gql.operations.app.listAppsByWorkspaceOwner(workspaceId);

      return result.filter(exists);
    },
  });
};

export const useQueryListAppLicensesByLicensee = (
  licenseeWorkspaceId: string | undefined,
) => {
  return useQuery({
    queryKey: ["app-licenses", licenseeWorkspaceId],
    enabled: licenseeWorkspaceId != null,
    queryFn: async () => {
      if (licenseeWorkspaceId == null) return;

      const result = await gql.operations.app.listAppLicensesByLicensee({
        licenseeWorkspaceId,
      });

      return result.filter(exists);
    },
  });
};
export const useMutationPutAppUserConfig = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (appUser: CreateAppUserConfigInput): Promise<void> => {
      await gql.operations.app.putAppUserConfig({
        ...appUser,
        schemaVersion: "2020-01-01",
      });

      client.invalidateQueries(["app-user-config", appUser.id]);
    },
  });
};

export const useMutationResetAppUserAction = () => {
  return useMutation({
    mutationFn: async (input: { appKey: string; userId: string }) => {
      const result = (await GraphQLAPI.query({
        query: /* GraphQL */ `
          mutation ResetAppUser($input: ControllerInput!) {
            resetAppUserAction(input: $input) {
              id
              sk
            }
          }
        `,
        variables: {
          input: {
            action: "resetAppUser",
            params: JSON.stringify(input),
          },
        },
      })) as any;

      return result.data.resetAppUserAction;
    },
  });
};

export const useMutationDeleteAppUserAction = () => {
  return useMutation({
    mutationFn: async (input: { appKey: string; userId: string }) => {
      const result = (await GraphQLAPI.query({
        query: /* GraphQL */ `
          mutation DeleteAppUser($input: ControllerInput!) {
            deleteAppUserAction(input: $input) {
              id
              sk
            }
          }
        `,
        variables: {
          input: {
            action: "deleteAppUser",
            params: JSON.stringify(input),
          },
        },
      })) as any;

      return result.data.deleteAppUserAction;
    },
  });
};
